import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import LangSwitcher from "@/components/LangSwitcher";
import { useI18n } from "@/i18n/useI18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Zap, ArrowRight, Sparkles, Copy, Loader2, Wand2, CheckCircle2, Rocket,
  X, Facebook, Linkedin, ShieldCheck, Clock, Ban, CalendarCheck,
} from "lucide-react";

const handleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const FUNNEL_EN_PATH = "/start";
const FUNNEL_ES_PATH = "/es/start";

const SAMPLE_ICON = { facebook: Facebook, linkedin: Linkedin };

// Example scans shown before the visitor runs their own — Facebook & LinkedIn only.
const DEMO_SCANS = [
  {
    brand: { brand_name_detected: "Northwind Coffee", brand_accent: "#b5651d" },
    posts: [
      { platform: "facebook", platform_label: "Facebook", headline: "4,000 cups poured", subheadline: "Roasted the morning it ships.", post_text: "We roast every batch the morning it ships, so your first cup tastes exactly like it should. Free shipping on your first bag this week.", hashtags: ["#specialtycoffee", "#freshroast"] },
      { platform: "linkedin", platform_label: "LinkedIn", headline: "Bean to doorstep in 48h", subheadline: "How we halved roast-to-ship time.", post_text: "Freshness is a supply-chain problem. Here's how we rebuilt ours to get coffee to you within 48 hours of roasting.", hashtags: ["#operations", "#coffee"] },
      { platform: "facebook", platform_label: "Facebook", headline: "Your morning, upgraded", subheadline: "Single-origin, ground to order.", post_text: "Swap the gas-station drip for something you'll actually look forward to. Single-origin, ground to order, delivered monthly.", hashtags: ["#subscription", "#coffeelover"] },
    ],
  },
  {
    brand: { brand_name_detected: "Lumen Skincare", brand_accent: "#1ba3d6" },
    posts: [
      { platform: "facebook", platform_label: "Facebook", headline: "1 drop, 8 hours of glow", subheadline: "Clinically-tested hyaluronic serum.", post_text: "Your skin drinks it in — 92% of testers saw visibly smoother skin in 14 days. No fillers, no fragrance, just results.", hashtags: ["#skincare", "#hyaluronic"] },
      { platform: "linkedin", platform_label: "LinkedIn", headline: "Why we ditched fillers", subheadline: "The formulation decision behind our serum.", post_text: "Most serums are 80% filler. We stripped ours back to the actives that actually work — here's the thinking behind it.", hashtags: ["#beauty", "#formulation"] },
      { platform: "facebook", platform_label: "Facebook", headline: "14 days to smoother skin", subheadline: "Backed by a real clinical panel.", post_text: "We put our serum in front of 100 testers for two weeks. The results made us rethink what a lightweight formula can do.", hashtags: ["#glowup", "#cleanbeauty"] },
    ],
  },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function StartFunnel() {
  const { t, lang } = useI18n();
  const [params] = useSearchParams();
  const src = params.get("src") || "";
  const ref = params.get("ref") || "";
  const [barOpen, setBarOpen] = useState(true);
  const siteOrigin = typeof window !== "undefined" ? window.location.origin : "https://autogridsocial.com";

  // ---------- URL scan preview (the core lead magnet) ----------
  const [url, setUrl] = useState("");
  const [posts, setPosts] = useState([]);
  const [brand, setBrand] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [demoIdx, setDemoIdx] = useState(0);
  const showDemo = !busy && posts.length === 0;

  useEffect(() => {
    if (!showDemo) return;
    const iv = setInterval(() => setDemoIdx((d) => (d + 1) % DEMO_SCANS.length), 5000);
    return () => clearInterval(iv);
  }, [showDemo]);

  const displayPosts = posts.length ? posts : DEMO_SCANS[demoIdx].posts;
  const displayBrand = posts.length ? brand : DEMO_SCANS[demoIdx].brand;

  const visitTracked = React.useRef(false);
  useEffect(() => {
    if (!visitTracked.current) {
      visitTracked.current = true;
      api.post("/funnel/track", { event: "visit", src, ref }).catch(() => {});
    }
  }, [src, ref]);

  const startTrial = () => {
    const u = url.trim();
    try {
      if (u.length >= 3) localStorage.setItem("ags_scan_url", u);
    } catch (e) {}
    api.post("/funnel/track", { event: "trial_cta", src, ref }).catch(() => {});
    handleLogin();
  };

  const runScan = async () => {
    const u = url.trim();
    if (u.length < 3) { setErr("Enter your website URL (e.g. yourbrand.com)."); return; }
    setBusy(true); setErr(""); setPosts([]); setBrand(null);
    api.post("/funnel/track", { event: "url_scan", src, ref }).catch(() => {});
    try {
      const r = await api.post("/tools/url-scan", { url: u });
      setPosts(r.data.posts || []);
      setBrand(r.data.brand || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not scan that site right now. Please try again.");
    }
    setBusy(false);
  };
  const copy = async (txt) => { try { await navigator.clipboard.writeText(txt); toast.success("Copied"); } catch (e) {} };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="start-funnel">
      <Helmet>
        <title>Scan Your Website → A 30-Day Facebook & LinkedIn Campaign | AutoGridSocial</title>
        <meta name="description" content="Paste your store URL and watch AutoGridSocial generate a real, anti-slop Facebook & LinkedIn feed in seconds — then auto-schedule a full 30-day campaign. Plans from $29/mo." />
        <link rel="canonical" href={siteOrigin + (lang === "es" ? FUNNEL_ES_PATH : FUNNEL_EN_PATH)} />
        <link rel="alternate" hrefLang="en" href={siteOrigin + FUNNEL_EN_PATH} />
        <link rel="alternate" hrefLang="es" href={siteOrigin + FUNNEL_ES_PATH} />
        <link rel="alternate" hrefLang="x-default" href={siteOrigin + FUNNEL_EN_PATH} />
        <meta property="og:title" content="Scan Your Website → A 30-Day Facebook & LinkedIn Campaign | AutoGridSocial" />
        <meta property="og:description" content="Paste your URL, see a real anti-slop feed in seconds, then auto-schedule a full month to Facebook & LinkedIn." />
      </Helmet>

      {/* Top notification bar */}
      {barOpen && (
        <div className="relative bg-primary text-white text-center text-xs sm:text-sm px-8 py-2.5" data-testid="funnel-topbar">
          <span className="font-medium">{t("funnel.topbar", "No signup for the demo — scan your site and see a real Facebook & LinkedIn feed in seconds.")}</span>
          <button onClick={() => setBarOpen(false)} className="absolute right-3 top-1/2 -translate-y-1/2 opacity-80 hover:opacity-100" data-testid="funnel-topbar-close" aria-label="Dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="funnel-logo">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center"><Zap className="h-5 w-5 text-white" /></div>
            <span className="font-display font-bold text-lg tracking-tight">AutoGridSocial</span>
          </Link>
          <div className="flex items-center gap-3">
            <LangSwitcher />
            <Button data-testid="funnel-nav-cta" onClick={startTrial} className="bg-primary hover:bg-primary/90 text-white text-sm">{t("nav.trial", "Get started")}</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 grid-noise pointer-events-none" />
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 h-[440px] w-[880px] rounded-full bg-primary/10 blur-[130px] pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 text-center">
          <Badge className="mb-5 bg-secondary text-foreground border-border font-mono text-xs">
            <Sparkles className="h-3.5 w-3.5 mr-1.5 text-accent" /> {t("funnel.hero_badge", "Free to try · No card for the demo")}
          </Badge>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.08] mb-5" data-testid="funnel-headline">
            {t("funnel.hero_h1", "Paste your URL. Get a month of Facebook & LinkedIn posts.")}
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-xl mx-auto mb-8 leading-relaxed">
            {t("funnel.hero_sub", "AutoGridSocial reads your brand, writes concrete anti-slop posts, designs the graphics, and auto-schedules a full 30-day campaign at each platform's peak times. See a real preview below — no signup.")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" onClick={() => scrollTo("live-scan")} data-testid="funnel-hero-scan" className="bg-primary hover:bg-primary/90 text-white group">
              <Wand2 className="h-4 w-4 mr-2" /> {t("funnel.hero_cta1", "Analyze my Brand DNA — free")}
            </Button>
            <Button size="lg" variant="outline" onClick={() => scrollTo("pricing")} data-testid="funnel-hero-pricing" className="border-border">
              {t("funnel.hero_cta2", "See pricing")} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Live URL scan preview */}
      <section id="live-scan" className="border-b border-border/60 bg-card/30 scroll-mt-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-8">
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-accent">{t("funnel.scan_kicker", "Live preview · no signup")}</span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-2">{t("funnel.scan_title", "Enter your store URL. Watch it build your feed.")}</h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">{t("funnel.scan_sub", "We read your brand from your site and instantly generate a real, anti-slop 3-post feed for Facebook and LinkedIn — the same engine behind your full 30-day campaign.")}</p>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 sm:p-7">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
              <div className="flex-1 min-w-0">
                <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{t("funnel.scan_label", "Your Business Website")}</label>
                <input data-testid="funnel-scan-input" value={url} onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && runScan()}
                  placeholder="Paste your website link to extract your brand voice & offer..."
                  className="mt-1.5 w-full h-12 rounded-lg border border-border bg-background px-4 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/50" />
              </div>
              <Button data-testid="funnel-scan-btn" onClick={runScan} disabled={busy}
                className="h-12 px-6 bg-primary hover:bg-primary/90 text-white shrink-0">
                {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("funnel.scan_busy", "Analyzing…")}</> : <><Wand2 className="mr-2 h-4 w-4" /> {t("funnel.scan_go", "Analyze Brand DNA →")}</>}
              </Button>
            </div>
            {brand?.brand_name_detected && (
              <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground" data-testid="funnel-scan-brand">
                {brand.brand_logo_url && <img src={brand.brand_logo_url} alt="" className="h-5 w-5 rounded object-contain bg-white/80" />}
                {t("funnel.scan_detected", "Detected brand:")} <span className="text-foreground font-medium">{brand.brand_name_detected}</span>
                {brand.brand_accent && <span className="inline-block h-3 w-3 rounded-full border border-border" style={{ backgroundColor: brand.brand_accent }} />}
              </div>
            )}
            {err && <p className="text-sm text-rose-400 mt-4" data-testid="funnel-scan-error">{err}</p>}

            <div className="mt-6" data-testid="funnel-scan-cards">
              {busy ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[0, 1, 2].map((i) => <div key={i} className="rounded-xl border border-border bg-background/60 h-56 animate-pulse" />)}
                </div>
              ) : (
                <>
                  {showDemo && (
                    <div className="flex items-center justify-center gap-2 mb-4 text-xs text-muted-foreground" data-testid="funnel-scan-demo-label">
                      <span className="inline-block h-2 w-2 rounded-full animate-pulse" style={{ background: displayBrand?.brand_accent }} />
                      {t("funnel.scan_example", "Example —")} <span className="text-foreground font-medium">{displayBrand?.brand_name_detected}</span>. {t("funnel.scan_example2", "Scan your own site above for yours.")}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {displayPosts.map((p, i) => {
                      const Icon = SAMPLE_ICON[p.platform] || Facebook;
                      const bname = displayBrand?.brand_name_detected || "Your Brand";
                      const accent = displayBrand?.brand_accent || "#6366F1";
                      const initial = (bname[0] || "B").toUpperCase();
                      const headline = p.headline || (p.post_text || "").slice(0, 48);
                      return (
                        <div key={`${showDemo ? "d" + demoIdx : "r"}-${i}`} className="rounded-xl border border-border bg-card overflow-hidden flex flex-col animate-fadeUp" style={{ animationDelay: `${i * 0.08}s` }} data-testid={`funnel-scan-card-${i}`}>
                          <div className="flex items-center justify-between px-4 pt-4 pb-3">
                            <div className="flex items-center gap-2.5 min-w-0">
                              {displayBrand?.brand_logo_url ? (
                                <img src={displayBrand.brand_logo_url} alt="" className="h-8 w-8 rounded-lg object-contain bg-white/90 shrink-0" />
                              ) : (
                                <div className="h-8 w-8 rounded-lg grid place-items-center shrink-0 text-white font-display font-extrabold text-sm" style={{ background: accent }}>{initial}</div>
                              )}
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-foreground leading-tight truncate">{bname}</div>
                                <div className="flex items-center gap-1 text-[11px] text-muted-foreground"><Icon className="h-3 w-3" /> {p.platform_label || "Sponsored"}</div>
                              </div>
                            </div>
                            <button onClick={() => copy(p.post_text)} className="text-muted-foreground hover:text-primary shrink-0" data-testid={`funnel-scan-copy-${i}`}><Copy className="h-3.5 w-3.5" /></button>
                          </div>
                          <div className="relative aspect-[1200/630] mx-4 rounded-lg overflow-hidden border border-white/10 shadow-lg shadow-black/30 bg-[#0b0b16]"
                            style={p.image_url ? undefined : { background: `radial-gradient(130% 130% at 80% -10%, ${accent} 0%, #16172e 48%, #0b0b16 100%)` }}>
                            {p.image_url && (
                              <img src={p.image_url} alt="" className="absolute inset-0 w-full h-full object-cover"
                                onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            )}
                            <div className="absolute inset-0 pointer-events-none"
                              style={{ background: p.image_url
                                ? `linear-gradient(120deg, ${accent}e6 0%, ${accent}66 36%, rgba(11,11,22,0.55) 72%, rgba(11,11,22,0.30) 100%)`
                                : undefined }} />
                            <div className="absolute inset-0 opacity-[0.10] pointer-events-none" style={{ backgroundImage: "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)", backgroundSize: "22px 22px" }} />
                            <div className="relative h-full flex flex-col justify-between p-4">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-[8px] font-semibold tracking-[0.16em] uppercase px-2 py-1 rounded-full border border-white/30 bg-white/10 text-white">{p.platform_label}</span>
                                <span className="font-display font-extrabold text-white/90 text-[11px] truncate max-w-[45%]">{bname}</span>
                              </div>
                              <div>
                                <div className="font-display text-base sm:text-lg font-extrabold text-white leading-[1.06] tracking-tight drop-shadow-[0_2px_12px_rgba(0,0,0,0.4)]">{headline}</div>
                                {p.subheadline && <div className="text-white/80 text-[11px] mt-1.5 leading-snug">{p.subheadline}</div>}
                              </div>
                              <div className="h-1 w-10 rounded-full bg-white/90" />
                            </div>
                          </div>
                          <div className="px-4 pt-3 pb-4 flex flex-col flex-1">
                            <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap flex-1">{p.post_text}</p>
                            {p.hashtags?.length > 0 && <p className="text-xs font-mono text-accent mt-3">{p.hashtags.join(" ")}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 rounded-xl border border-primary/30 bg-primary/5 p-4">
                    <p className="text-sm text-foreground/90">
                      {showDemo
                        ? <span>{t("funnel.scan_up_demo", "This is a live example.")} <span className="font-semibold text-primary">{t("funnel.scan_up_demo2", "Scan your own store")}</span> {t("funnel.scan_up_demo3", "to see your feed, then auto-schedule a full month.")}</span>
                        : <span>{t("funnel.scan_up_real", "That's 3 of")} <span className="font-semibold text-primary">30</span>. {t("funnel.scan_up_real2", "Auto-schedule a full month to Facebook & LinkedIn in one click.")}</span>}
                    </p>
                    <Button data-testid="funnel-scan-upsell-cta" onClick={startTrial} className="bg-primary hover:bg-primary/90 text-white shrink-0 group">{t("funnel.scan_up_cta", "Build my 30-day campaign")} <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Trust / proof strip */}
      <section className="border-b border-border/60 scroll-mt-20" data-testid="funnel-trust">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Ban, stat: t("funnel.trust_1_stat", "0% clichés"), label: t("funnel.trust_1_label", "Anti-slop copy — no \u201cgame-changer\u201d filler") },
              { icon: CalendarCheck, stat: t("funnel.trust_2_stat", "Peak-time"), label: t("funnel.trust_2_label", "Auto-scheduled when your audience is live") },
              { icon: ShieldCheck, stat: t("funnel.trust_3_stat", "FB + LinkedIn"), label: t("funnel.trust_3_label", "Built for the two highest-intent feeds") },
              { icon: Clock, stat: t("funnel.trust_4_stat", "1-click cancel"), label: t("funnel.trust_4_label", "Month-to-month · cancel anytime") },
            ].map((s, i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-4 text-center" data-testid={`funnel-trust-${i}`}>
                <s.icon className="h-5 w-5 text-primary mx-auto mb-2" />
                <div className="font-display text-lg font-bold">{s.stat}</div>
                <div className="text-[11px] text-muted-foreground mt-1 leading-snug">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <PricingBlock onTrial={startTrial} t={t} />

      {/* Sticky mobile bottom bar */}
      <div className="fixed bottom-0 inset-x-0 z-40 sm:hidden border-t border-border bg-background/95 backdrop-blur-xl p-3" data-testid="funnel-sticky-bar">
        <Button onClick={() => scrollTo("live-scan")} className="w-full h-12 bg-primary hover:bg-primary/90 text-white text-base" data-testid="funnel-sticky-cta">
          {t("funnel.sticky_cta", "Analyze my Brand DNA — free")}
        </Button>
      </div>

      {/* Footer */}
      <footer className="border-t border-border py-10 pb-24 sm:pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-md bg-primary flex items-center justify-center"><Zap className="h-3.5 w-3.5 text-white" /></div>
            <span className="font-display font-semibold text-foreground">AutoGridSocial</span>
          </div>
          <span>© {new Date().getFullYear()} AutoGridSocial. Anti-slop AI social posts on autopilot.</span>
        </div>
      </footer>
    </div>
  );
}

function PricingBlock({ onTrial, t }) {
  const plans = [
    { key: "creator", name: "Creator", price: 29, cadence: "/mo",
      tagline: t("funnel.tier_creator_tag", "For solo creators & founders"),
      cta: t("funnel.tier_creator_cta", "Choose Creator"),
      features: [
        { text: "Unlimited 1-Click 30-Day Campaigns", included: true },
        { text: "Facebook & LinkedIn publishing", included: true },
        { text: "Automated peak-time scheduling", included: true },
        { text: "Up to 5 connected profiles", included: true },
        { text: "Shopify sync & AI Consultant", included: false },
      ] },
    { key: "growth", name: "E-commerce Growth", price: 79, cadence: "/mo", popular: true,
      tagline: t("funnel.tier_growth_tag", "For Shopify & online stores"),
      cta: t("funnel.tier_growth_cta", "Choose Growth"),
      features: [
        { text: "Everything in Creator", included: true },
        { text: "Shopify catalog sync", included: true },
        { text: "Inventory-aware auto-swap", included: true },
        { text: "AI Strategy Consultant (50/mo)", included: true },
        { text: "Up to 10 connected profiles", included: true },
      ] },
    { key: "agency", name: "Agency", price: 199, cadence: "/mo",
      tagline: t("funnel.tier_agency_tag", "For agencies & teams"),
      cta: t("funnel.tier_agency_cta", "Choose Agency"),
      features: [
        { text: "Everything in E-commerce Growth", included: true },
        { text: "Unlimited AI Strategy Consultant", included: true },
        { text: "Up to 25 connected profiles", included: true },
        { text: "Team seats & white-label client review", included: true },
        { text: "Priority support", included: true },
      ] },
  ];
  return (
    <section id="pricing" className="border-b border-border/60 bg-card/30 scroll-mt-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-8">
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight uppercase">{t("funnel.price_title", "Simple, transparent pricing")}</h2>
          <p className="text-muted-foreground mt-3">{t("funnel.price_sub", "Turn any website URL into a 30-day Facebook & LinkedIn campaign.")}</p>
          <p className="text-sm text-emerald-400 mt-2">{t("funnel.price_trial", "Every plan starts with a 7-day trial for $1 — your full 30-day campaign included. Cancel anytime.")}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 items-stretch">
          {plans.map((tier) => (
            <div key={tier.key} className={`rounded-2xl border p-6 relative flex flex-col ${tier.popular ? "border-primary bg-primary/5" : "border-border bg-card"}`} data-testid={`funnel-tier-${tier.key}`}>
              {tier.popular && <Badge className="absolute -top-3 left-6 bg-primary text-white border-0">{t("funnel.tier_popular", "★ Most Popular")}</Badge>}
              <h3 className="font-display text-xl font-bold">{tier.name}</h3>
              <p className="text-xs text-muted-foreground mt-1 mb-4">{tier.tagline}</p>
              <div className="flex items-end gap-1 mb-5">
                <span className="font-display text-4xl font-extrabold" data-testid={`funnel-price-${tier.key}`}>${tier.price}</span>
                <span className="text-muted-foreground text-sm mb-1">{tier.cadence}</span>
              </div>
              <ul className="space-y-2 mb-6 flex-1">
                {tier.features.map((f, i) => (
                  <li key={i} className={`flex items-start gap-2 text-sm ${f.included ? "" : "text-muted-foreground/60"}`}>
                    {f.included
                      ? <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      : <X className="h-4 w-4 text-rose-400/70 mt-0.5 shrink-0" />}
                    {f.text}
                  </li>
                ))}
              </ul>
              <Button onClick={onTrial} className={`w-full ${tier.popular ? "bg-primary hover:bg-primary/90 text-white" : "bg-secondary hover:bg-secondary/80 text-foreground"}`} data-testid={`funnel-tier-cta-${tier.key}`}>
                <Rocket className="h-4 w-4 mr-2" /> {tier.cta}
              </Button>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-muted-foreground mt-8">🔒 {t("funnel.price_trust", "Instant access. Cancel or top up anytime.")}</p>
      </div>
    </section>
  );
}
